import { Avatar, Box, Stack, Typography } from '@mui/material';
import { ALLOWED_FILE_TYPES } from 'Config';
import { useDictionary } from 'Hooks';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { PlusBtn, UploadFile } from 'Images';
import { FC, useRef } from 'react';

interface IFileUploadZone {
  onFileSelect: (files: FileList) => void;
  acceptedFileTypes?: string;
  hasUploadedFiles?: boolean;
}

export const FileUploadZone: FC<IFileUploadZone> = ({
  onFileSelect,
  acceptedFileTypes = ALLOWED_FILE_TYPES,
  hasUploadedFiles = false,
}) => {
  const dictionary = useDictionary(Dictionary, DICTIONARY.UniversalRequest);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      onFileSelect(files);
    }
    if (event.target) {
      event.target.value = '';
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      onFileSelect(files);
    }
  };

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        border: '2px dashed #5B86EF70',
        borderRadius: '12px',
        backgroundColor: '#F6F8FE',
        padding: hasUploadedFiles ? '14px 0px' : '29px 0px',
        textAlign: 'center',
        transition: 'all 0.2s ease-in-out',
        pointerEvents: 'auto',
        cursor: 'pointer',
      }}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleButtonClick}
    >
      <Stack
        gap='12px'
        alignItems='center'
        justifyContent='center'
        direction='row'
        sx={{ pointerEvents: 'none' }}
      >
        <Avatar
          alt={dictionary.uploadFile.uploadFilesAlt}
          src={hasUploadedFiles ? PlusBtn : UploadFile}
          sx={{
            width: hasUploadedFiles ? '20px' : '48px',
            height: hasUploadedFiles ? '20px' : '48px',
          }}
        />
        <Stack width='fit-content'>
          <Typography
            sx={{ alignSelf: 'flex-start' }}
            variant={hasUploadedFiles ? 'body5' : 'body1'}
          >
            {dictionary.uploadFile.attachDocument}
          </Typography>
          {!hasUploadedFiles && (
            <Typography variant='body6'>
              {dictionary.uploadFile.attachOneOrMore}
            </Typography>
          )}
        </Stack>

        <input
          ref={fileInputRef}
          type='file'
          accept={acceptedFileTypes}
          multiple
          hidden
          onChange={handleFileChange}
          aria-label={dictionary.uploadFile.selectFilesAriaLabel}
        />
      </Stack>
    </Box>
  );
};
